import { FileRejection } from "react-dropzone";
import { formatBytes } from "@/app/(signed-in)/create/shared/formatting-helpers";

export const acceptedFileFormat = {
  audio: {
    "audio/mp3": [".mp3"],
    "audio/mpeg": [".mpeg"],
    "audio/wav": [".wav"],
    "audio/aac": [".aac"],
    "audio/aiff": [".aiff"],
    "audio/flac": [".flac"],
  },
  image: {
    "image/png": [".png"],
    "image/jpg": [".jpg"],
    "image/jpeg": [".jpeg"],
    // heic does not render in the browser on desktop, and does not work with OffscreenCanvas
    // "image/heic": [],
    "image/webp": [".webp"],
    "image/avif": [".avif"],
    "image/jp2": [".jp2"],
    "image/tiff": [".tiff"],
    "image/bmp": [".bmp"],
  },
  video: {
    "video/mp4": [".mp4", ".m4v"],
    "video/quicktime": [".mov"],
    "video/webm": [".webm"],

    // these are not supported by the browser so we can't play them, and can't parse height/width/fps
    // "video/x-msvideo": [".avi"],
    // "video/x-matroska": [".mkv"],
    // "video/x-flv": [".flv"],
    // "video/mpeg": [".mpeg"],
    // "video/x-ms-wmv": [".wmv"],

    // hevc does not render in browser, and `file.type` returns empty string
    // "video/hevc": [".hevc"],
  },
};

export const acceptedFileExtensions = {
  video: Object.values(acceptedFileFormat.video)
    .flat()
    .map((ext) => ext.replace(".", "")),
  audio: Object.values(acceptedFileFormat.audio)
    .flat()
    .map((ext) => ext.replace(".", "")),
  image: Object.values(acceptedFileFormat.image)
    .flat()
    .map((ext) => ext.replace(".", "")),
};

type ValidAssetType = keyof typeof acceptedFileFormat;

export function isValidAssetType(type: string): type is ValidAssetType {
  return acceptedFileFormat.hasOwnProperty(type);
}

/**
 * checks if the passed in file extension is invalid.
 *
 * @returns {boolean} true if extension is in valid
 */
export function isInvalidFileExtension(
  type: ValidAssetType,
  extension: string | undefined
): boolean {
  if (!extension) return true;

  return acceptedFileExtensions[type].includes(extension.toLowerCase()) === false;
}

export function DropzoneError({
  fileRejections,
  maxFileSize,
  type,
}: {
  fileRejections: readonly FileRejection[];
  maxFileSize?: number;
  type: keyof typeof acceptedFileFormat;
}) {
  return fileRejections.map(({ file, errors }) => {
    return (
      <div key={file.name} className="mt-2">
        {errors.map((e) => {
          if (e.code === "file-too-large") {
            if (!maxFileSize) return;

            const formattedSize = formatBytes(maxFileSize);

            return (
              <div key={e.code} className="text-center text-sm text-red-500">
                {`Max file size is ${formattedSize}`}
              </div>
            );
          }

          if (e.code === "file-invalid-type") {
            const formats = acceptedFileFormat[type];
            return (
              <div key={e.code} className="text-center text-sm text-red-500">
                {`File type must be ${Object.values(formats).flat().join(", ")}`}
              </div>
            );
          }
        })}
      </div>
    );
  });
}
